import {debounce} from "lodash";
import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {ChannelContext} from "../../../../Contexts/ChannelContext";
import {UserContext} from "../../../../Contexts/UserContext";
import {MAX_CART_ITEM_QUANTITY} from "../../QuantityLimit/quantityLimit";
import {calculateOrderQuantity} from "../logic/calculateOrderQuantity";
import {createAddProductToCartPayload} from "../logic/createAddProductToCartPayload";
import {triggerCartLogin} from "../logic/triggerCartLogin";
import {updateProductCartQuantity} from "../logic/updateProductCartQuantity";
import {AddProductToCartPayload, QuantityFieldsData} from "../types";

export function useCartItemQuantity(data: QuantityFieldsData): {
    calculatedQuantity: number | string;
    hasMinimumOrderQuantityAdjustment: boolean;
    hasQuantityLimitError: boolean;
    maxInputQuantity: number;
    handleInput: (event: React.FormEvent<HTMLInputElement>) => void;
    handleKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    handleWheel: (event: React.WheelEvent<HTMLInputElement>) => false;
    inputRef: React.MutableRefObject<HTMLInputElement>;
} {
    const {t} = useTranslation();
    const {user} = useContext<any>(UserContext);
    const channelContext = useContext(ChannelContext);
    const inputRef = useRef<any>();
    const [quantity, setQuantity] = useState<number | string>(data.product.cartItemQuantity);
    const [hasQuantityLimitError, setHasQuantityLimitError] = useState(false);
    const requestVersionRef = useRef(0);
    const lastAcceptedQuantityRef = useRef<number | string>(data.product.cartItemQuantity);
    const localValidationErrorRef = useRef(false);
    const requestQueueRef = useRef<Promise<void>>(Promise.resolve());
    const dataRef = useRef(data);
    dataRef.current = data;
    const allowsIncompletePackaging = channelContext?.IncompletePackaging === true
        && !channelContext.products.some((product) => product.id === data.product.id);
    const maxInputQuantity = allowsIncompletePackaging
        ? MAX_CART_ITEM_QUANTITY
        : Math.floor(MAX_CART_ITEM_QUANTITY / Math.max(data.product.quantityPackage, 1));

    useEffect(() => {
        inputRef.current.value = data.product.cartItemQuantity === 0 ? '' : data.product.cartItemQuantity;
    }, []);

    useEffect(() => {
        requestVersionRef.current += 1;
        localValidationErrorRef.current = false;

        if (user === false){
            inputRef.current.value = '';
            setQuantity(0);
            lastAcceptedQuantityRef.current = 0;
            setHasQuantityLimitError(false);
        } else {
            inputRef.current.value = data.product.cartItemQuantity === 0 ? '' : data.product.cartItemQuantity;
            setQuantity(data.product.cartItemQuantity === 0 ? '' : data.product.cartItemQuantity);
            lastAcceptedQuantityRef.current = data.product.cartItemQuantity;
            setHasQuantityLimitError(false);
        }
    }, [user]);

    const addProductToCartDebounced = useMemo(() => debounce((payload: AddProductToCartPayload, value: number | string, requestVersion: number) => {
            requestQueueRef.current = requestQueueRef.current
                .then(() => dataRef.current.addProductToCart(payload))
                .then((result) => {
                    if (result.isSuccess) {
                        lastAcceptedQuantityRef.current = value;

                        if (requestVersionRef.current === requestVersion && !localValidationErrorRef.current) {
                            setHasQuantityLimitError(false);
                        }
                    } else if (result.quantityLimitExceeded && requestVersionRef.current === requestVersion) {
                        const acceptedQuantity = lastAcceptedQuantityRef.current;
                        const restoredValue = acceptedQuantity === 0 ? '' : String(acceptedQuantity);

                        inputRef.current.value = restoredValue;
                        setQuantity(restoredValue);
                        updateProductCartQuantity(
                            dataRef.current.products,
                            dataRef.current.product.id,
                            acceptedQuantity,
                            dataRef.current.setProducts
                        );
                        setHasQuantityLimitError(true);
                    }
                })
                .catch(() => {
                    // Leave non-limit request failures to the existing request flow.
                });
        }, 1000),
        []
    );

    useEffect(() => () => {
        requestVersionRef.current += 1;
        addProductToCartDebounced.cancel();
    }, [addProductToCartDebounced]);

    function handleWheel(event: React.WheelEvent<HTMLInputElement>): false {
        event.currentTarget.blur();
        event.preventDefault();

        return false;
    }

    function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
        const blocked = ['.', ',', 'e', 'E'];

        if (blocked.includes(event.key) && (channelContext?.IncompletePackaging === false || channelContext?.products.some((product) => product.id === data.product.id))) {
            event.preventDefault();
        }

        const inputs = document.querySelectorAll('.list .product input[name="quantity"]') as NodeListOf<HTMLInputElement>;
        const currentIndex = Array.from(inputs).indexOf(event.currentTarget);

        if (event.key === 'ArrowDown') {
            if (currentIndex >= 0 && currentIndex < inputs.length - 1) {
                inputs[currentIndex + 1].focus();
            }

            event.preventDefault();
        }

        if (event.key === 'ArrowUp'){
            if (currentIndex !== 0) {
                inputs[currentIndex - 1].focus();
            }

            event.preventDefault();
        }
    }

    function handleInput(event: any): void {
        const blocked = ['.', ',', 'e', 'E'];

        if (blocked.includes(event.key) && (channelContext?.IncompletePackaging === false || channelContext?.products.some((product) => product.id === data.product.id))) {
            event.preventDefault();
        }

        const nextValue = event.currentTarget.value;
        const nextQuantityInPieces = calculateOrderQuantity(nextValue, data.product, channelContext).quantityInPieces;
        if (Number(nextValue) > maxInputQuantity || (nextQuantityInPieces !== null && nextQuantityInPieces > MAX_CART_ITEM_QUANTITY)) {
            event.currentTarget.value = quantity === 0 ? '' : String(quantity);
            localValidationErrorRef.current = true;
            setHasQuantityLimitError(true);
            return;
        }

        const requestVersion = ++requestVersionRef.current;
        localValidationErrorRef.current = false;
        setHasQuantityLimitError(false);

        if (user === false){
            triggerCartLogin(
                t('layout.login-form.buy-not-logged'),
                inputRef,
                data.product,
                data.products,
                data.setProducts
            );
            event.preventDefault();
        }

        setQuantity(event.currentTarget.value);
        updateProductCartQuantity(data.products, data.product.id, event.currentTarget.value, data.setProducts);
        addProductToCartDebounced(
            createAddProductToCartPayload(data.product, event.currentTarget.value),
            event.currentTarget.value,
            requestVersion
        );
    }

    const orderQuantityCalculation = calculateOrderQuantity(quantity, data.product, channelContext);

    return {
        calculatedQuantity: orderQuantityCalculation.calculatedQuantity,
        hasMinimumOrderQuantityAdjustment: orderQuantityCalculation.hasMinimumOrderQuantityAdjustment,
        hasQuantityLimitError,
        maxInputQuantity,
        handleInput,
        handleKeyDown,
        handleWheel,
        inputRef
    };
}
